import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { buildApiUrl } from "@/api/terminalApi";

export type ApiState = "checking" | "up" | "down";

export interface ApiStatus {
	state: ApiState;
	/** Round trip of the last successful /health call, measured in this browser. */
	latencyMs: number | null;
	/** Last block the indexer reported through /block. */
	block: number | null;
	checkedAt: number | null;
	nextCheckAt: number | null;
	recheck: () => void;
}

const UP_INTERVAL_MS = 30_000;
const DOWN_INTERVAL_MS = 20_000;
const TIMEOUT_MS = 6_000;

const Ctx = createContext<ApiStatus>({
	state: "checking",
	latencyMs: null,
	block: null,
	checkedAt: null,
	nextCheckAt: null,
	recheck: () => {},
});

export const useApiStatus = () => useContext(Ctx);

async function probe(): Promise<{ latencyMs: number; block: number | null }> {
	const started = performance.now();
	const res = await fetch(buildApiUrl("/health"), { signal: AbortSignal.timeout(TIMEOUT_MS) });
	if (!res.ok) throw new Error(String(res.status));
	const latencyMs = Math.round(performance.now() - started);
	let block: number | null = null;
	try {
		const b = await fetch(buildApiUrl("/block"), { signal: AbortSignal.timeout(TIMEOUT_MS) });
		const n = Number(((await b.json()) as { block?: unknown }).block);
		block = Number.isFinite(n) && n > 0 ? n : null;
	} catch {
		// /health answered, so the API is up; the block is just unknown.
	}
	return { latencyMs, block };
}

export function ApiStatusProvider({ children }: { children: ComponentChildren }) {
	const [s, setS] = useState<Omit<ApiStatus, "recheck">>({
		state: "checking",
		latencyMs: null,
		block: null,
		checkedAt: null,
		nextCheckAt: null,
	});
	const timer = useRef<number | undefined>(undefined);
	const skipped = useRef(false);

	const run = useCallback(async () => {
		window.clearTimeout(timer.current);
		// A hidden tab does not poll. The visibility listener below picks up where this stops.
		if (document.visibilityState === "hidden") {
			skipped.current = true;
			return;
		}
		skipped.current = false;
		let wait = DOWN_INTERVAL_MS;
		try {
			const { latencyMs, block } = await probe();
			wait = UP_INTERVAL_MS;
			setS({ state: "up", latencyMs, block, checkedAt: Date.now(), nextCheckAt: Date.now() + wait });
		} catch {
			setS({ state: "down", latencyMs: null, block: null, checkedAt: Date.now(), nextCheckAt: Date.now() + wait });
		}
		timer.current = window.setTimeout(run, wait);
	}, []);

	useEffect(() => {
		void run();
		const onVisible = () => {
			if (document.visibilityState === "visible" && skipped.current) void run();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.clearTimeout(timer.current);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [run]);

	const value = useMemo(() => ({ ...s, recheck: run }), [s, run]);
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
